import { AuthenticatedAPIClient, type APIResponse } from "./authenticated-api";

/**
 * Global (not per-khoa) app settings. Today this is only the print-before-
 * completion gate, but the shape mirrors the backend's `AppSettings` so a
 * future key needs no new endpoint.
 */
export interface AppSettings {
	allowPrintBeforeCompletion: boolean;
}

export type AppSettingsUpdateInput = Partial<AppSettings>;

export interface CacheResetResult {
	/**
	 * Một số version cho mỗi lớp phụ thuộc của cache thống kê.
	 *
	 * Ba lớp vì ba nguồn làm số liệu cũ đi khác nhau: `reception` là trạng thái
	 * tiếp nhận, `fee` là học phí, `roster` là thêm/sửa/xoá sinh viên. Đồng bộ
	 * học phí chạy hàng giờ chỉ đụng `fee`, nên tách ra để nó thôi xoá luôn cache
	 * của giới tính, độ tuổi và nhân khẩu — những thứ nó không hề chạm tới.
	 */
	dataVersion: {
		reception: string;
		fee: string;
		roster: string;
	};
	redisAvailable: boolean;
}

/**
 * Unwraps the `{ success, message, data }` envelope every `/v1` endpoint
 * returns, throwing `Error(message)` on failure so every caller has one
 * catch shape instead of re-checking `success` everywhere.
 */
async function unwrap<T>(response: Promise<APIResponse<T>>): Promise<T> {
	const result = await response;
	if (!result.success || result.data === undefined) {
		throw new Error(result.message || "Yêu cầu thất bại");
	}
	return result.data;
}

/**
 * Thin typed client for `/v1/settings`. No component talks to `fetch` or
 * `AuthenticatedAPIClient` directly for this endpoint.
 */
class SettingsApiService {
	private readonly baseUrl = "/settings";

	get(): Promise<AppSettings> {
		return unwrap(AuthenticatedAPIClient.get<AppSettings>(this.baseUrl));
	}

	update(input: AppSettingsUpdateInput): Promise<AppSettings> {
		return unwrap(AuthenticatedAPIClient.patch<AppSettings>(this.baseUrl, input));
	}

	/** Xoá cache thống kê (tầng Redis dùng chung, qua bump version). */
	resetCache(): Promise<CacheResetResult> {
		return unwrap(AuthenticatedAPIClient.post<CacheResetResult>(`${this.baseUrl}/cache/reset`, {}));
	}
}

export const settingsApi = new SettingsApiService();
